import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Download, 
  Upload, 
  Tag, 
  FolderOpen, 
  Layers,
  ChevronRight,
  ChevronDown,
  Globe,
  Flame,
  Zap,
  Users,
  Shield,
  Target,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { enhancedTagService, 
  TagCategory, 
  TagSubcategory, 
  TagGroup, 
  EnhancedTag, 
  TagHierarchy,
  CustomTagData,
  CustomCategoryData,
  CustomSubcategoryData,
  CustomGroupData
} from '@/services/enhancedTagService';

interface TagTreeItem {
  id: string;
  name: string;
  description?: string;
  is_system: boolean;
  children?: TagTreeItem[];
  tags?: EnhancedTag[];
}

export const EnhancedTagManagementSystem = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  // State for data
  const [tagTree, setTagTree] = useState<{ categories: TagTreeItem[] }>({ categories: [] });
  const [allTags, setAllTags] = useState<TagHierarchy[]>([]);
  const [userTags, setUserTags] = useState<EnhancedTag[]>([]);
  const [statistics, setStatistics] = useState<any>({});
  
  // State for UI
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  
  // State for modals
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);
  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false);
  const [isCreateSubcategoryModalOpen, setIsCreateSubcategoryModalOpen] = useState(false);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  
  // State for forms
  const [tagForm, setTagForm] = useState<CustomTagData>({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  const [categoryForm, setCategoryForm] = useState<CustomCategoryData>({
    name: '',
    description: ''
  });
  const [subcategoryForm, setSubcategoryForm] = useState<CustomSubcategoryData>({
    name: '',
    description: '',
    category_id: ''
  });
  const [groupForm, setGroupForm] = useState<CustomGroupData>({
    name: '',
    description: '',
    subcategory_id: ''
  });
  const [importContent, setImportContent] = useState('');
  
  // State for loading
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [treeData, tagsData, userTagsData, statsData] = await Promise.all([
        enhancedTagService.getTagTree(),
        enhancedTagService.getTagsWithHierarchy(),
        enhancedTagService.getUserTags(user!.id),
        enhancedTagService.getTagStatistics()
      ]);

      setTagTree(treeData);
      setAllTags(tagsData);
      setUserTags(userTagsData);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading tag data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tag data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Enhanced Tag Management System</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Enhanced Tag Management System
          </CardTitle>
          <p className="text-sm text-gray-600">
            Comprehensive tag system for organizing emissions data with hierarchical categories, subcategories, and groups
          </p>
        </CardHeader>
        <CardContent>
          <div className="text-center p-8">
            <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Tag System Ready</h3>
            <p className="text-gray-600">
              The enhanced tag management system is ready to use. 
              Run the database migration first to create the required tables and system tags.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
